import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { logout } from "./api";
import { useAuth } from "./auth";
import logo from "./assets/estimate_tracker_logo.png";

const initials = (name) =>
  (name || "?")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const ROLE_LABEL = { admin: "Admin", employee: "Employee", client: "Client" };

/* Inline nav icons — 1.8 stroke, currentColor so active/hover states carry. */
const IconProjects = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);
const IconAnalytics = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v18h18" />
    <path d="M7 15l4-5 3 3 5-7" />
  </svg>
);
const IconUsers = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11" />
  </svg>
);
const IconSettings = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

export default function Layout() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isAdmin = user?.role === "admin";
  const projectsActive =
    pathname === "/" ||
    pathname.startsWith("/projects/") ||
    pathname.startsWith("/sheets/");

  const handleLogout = () => {
    logout();
    setUser(null);
    navigate("/login");
  };

  const roleLabel =
    isAdmin && user?.is_superadmin ? "Super admin" : ROLE_LABEL[user?.role] || "";

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <NavLink to="/" className="brand">
          <img className="brand-logo" src={logo} alt="" aria-hidden="true" />
          <span>Estimate Tracker</span>
        </NavLink>

        <nav className="side-nav">
          <NavLink to="/" end className={projectsActive ? "active" : ""}>
            <IconProjects />
            <span>Projects</span>
          </NavLink>
          {isAdmin && user?.is_superadmin && (
            <NavLink to="/analytics" className={({ isActive }) => (isActive ? "active" : "")}>
              <IconAnalytics />
              <span>Analytics</span>
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/users" className={({ isActive }) => (isActive ? "active" : "")}>
              <IconUsers />
              <span>Users</span>
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/settings" className={({ isActive }) => (isActive ? "active" : "")}>
              <IconSettings />
              <span>Settings</span>
            </NavLink>
          )}
        </nav>

        <div className="side-spacer" />

        <div className="side-foot">
          <div className="side-user">
            <span className="avatar">{initials(user?.name)}</span>
            <span className="who">
              <b>{user?.name}</b>
              {roleLabel && <span>{roleLabel}</span>}
            </span>
          </div>
          <button className="ghost side-signout" onClick={handleLogout}>Sign out</button>
        </div>
      </aside>

      <main className="container">
        <Outlet />
      </main>
    </div>
  );
}

export { initials };
